'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { changePassword } from '@eliadmin/core/src/api/customers/customers-api';
import { AccountCard } from '@eliadmin/core/src/components/dlz/account/account-card';
import { ACCOUNT_COPY } from '@eliadmin/core/src/components/dlz/account/account-copy';
import { PASSWORD_COPY } from '@eliadmin/core/src/components/dlz/account/password/password-copy';
import {
  CheckboxField,
  PasswordField,
  TextField,
} from '@eliadmin/core/src/components/dlz/account/password/password-fields';
import {
  passwordFormSchema,
  type PasswordFormValues,
} from '@eliadmin/core/src/components/dlz/account/password/password-schema';
import {
  FormResultMessage,
  type FormResult,
} from '@eliadmin/core/src/components/dlz/account/profile/form-result';
import { applyServerErrors } from '@eliadmin/core/src/components/dlz/account/profile/server-errors';
import { RESET_COPY } from '@eliadmin/core/src/components/dlz/auth/password-reset/password-reset-copy';
import { PasswordStrength } from '@eliadmin/core/src/components/dlz/forms/password-strength';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Form } from '@eliadmin/core/src/components/ui/form';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

const EMPTY: PasswordFormValues = {
  password_old: '',
  password: '',
  password_confirmation: '',
  logout_other_devices: true,
};

const FIELDS = Object.keys(EMPTY) as (keyof PasswordFormValues)[];

/** Figma „Input“ State=Success helper (60322:19962): 16 px check in status/success, text/success. */
function MatchHint() {
  return (
    <span className="flex items-center gap-1.5 text-[#16753A]">
      <Icon name="check" size={16} className="text-[#4DB873]" />
      {PASSWORD_COPY.match}
    </span>
  );
}

/**
 * Figma „Password form“ (60322:19900; mobile 60459:90321): current password, the new one with the
 * strength meter and the rules, the repeat with its success state once both match, „Odhlásiť ma zo
 * všetkých ostatných zariadení“ and „Zmeniť heslo“. A 422 goes onto the fields it names; a success
 * empties the form and is announced under the button until the next edit.
 */
export function PasswordForm({
  email,
  authToken,
  className,
}: {
  email: string;
  authToken: string;
  className?: string;
}) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const { formValidationMessages } = useFormValidationMessages(t);
  const [result, setResult] = useState<FormResult>(null);
  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordFormSchema(formValidationMessages, t('text1103'))),
    defaultValues: EMPTY,
  });
  const [password, confirmation] = useWatch({
    control: form.control,
    name: ['password', 'password_confirmation'],
  });
  const { isDirty, isSubmitting, errors } = form.formState;
  const matches = !!confirmation && confirmation === password && !errors.password_confirmation;

  const onSubmit = form.handleSubmit(async values => {
    setResult(null);
    try {
      // `logout_other_devices` goes beyond the core request type; the endpoint has to confirm it.
      const request = { ...values, authToken, locale };
      await changePassword(request);
      form.reset({ ...EMPTY, logout_other_devices: values.logout_other_devices });
      setResult({ status: 'success' });
    } catch (error) {
      const rest = applyServerErrors(error, form.setError, field =>
        FIELDS.find(name => name === field),
      );
      if (rest) setResult({ status: 'failed', message: rest.join('\n') || PASSWORD_COPY.failed });
    }
  });

  return (
    <AccountCard title={t('text203')} className={cn('gap-6', className)}>
      <Form {...form}>
        <form
          noValidate
          aria-label={ACCOUNT_COPY.pages.password.label}
          onSubmit={onSubmit}
          className="flex flex-col gap-6"
        >
          {/* Lets password managers file the new password under the account. */}
          <input
            type="email"
            name="username"
            autoComplete="username"
            value={email}
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            className="sr-only"
          />
          <div className="flex flex-col gap-4 md:gap-3">
            <PasswordField
              name="password_old"
              label={t('text3415')}
              required
              autoComplete="current-password"
            />
            <div className="flex flex-col gap-2">
              <PasswordField
                name="password"
                label={t('text203')}
                required
                autoComplete="new-password"
              />
              <PasswordStrength value={password} />
            </div>
            <TextField
              name="password_confirmation"
              type="password"
              label={PASSWORD_COPY.repeat}
              required
              autoComplete="new-password"
              hint={matches ? <MatchHint /> : undefined}
              after={
                matches ? <Icon name="check" size={20} className="text-[#4DB873]" /> : undefined
              }
              classNames={
                matches ? { wrapper: 'border-[#4DB873] hover:border-[#4DB873]' } : undefined
              }
            />
          </div>
          <CheckboxField name="logout_other_devices">{RESET_COPY.reset.logoutOthers}</CheckboxField>
          <div className="flex flex-col md:flex-row md:items-center md:gap-4">
            <Button
              type="submit"
              isLoading={isSubmitting}
              className="h-12 w-full md:h-[52px] md:w-auto"
            >
              {t('text187')}
            </Button>
            <FormResultMessage
              result={result?.status === 'success' && isDirty ? null : result}
              success={t('text649')}
              className="mt-3 empty:mt-0 md:mt-0 md:min-w-0 md:flex-1"
            />
          </div>
        </form>
      </Form>
    </AccountCard>
  );
}
